import dayjs from 'dayjs'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'

dayjs.extend(utc)
dayjs.extend(timezone)

// Market-agnostic temporal state of a subject's session, relative to `now`. This
// is the shared "pre/post-session" primitive (ADR 2026.07.27-001); feature
// resolvers (e.g. Senior Preorder) consume it rather than recomputing the
// date/no-show logic.
export enum SessionPhase {
  // Session date is today or in the future (still within the session day).
  PreSession = 'pre-session',
  // Session date has passed and the session happened.
  PostSession = 'post-session',
  // Session date has passed but the session did not happen (e.g. no-show).
  DidNotOccur = 'did-not-occur'
}

// The session day closes at 11:59 PM Eastern — the studio ordering convention
// (matches `OfferExpirationDate.vue` and the preorder offer window). Open item
// (ADR 2026.07.27-001): confirm whether this should be studio-local rather than a
// single ET zone for all markets.
const SESSION_DAY_TIMEZONE = 'America/New_York'

// Past-date sessions in these `SessionStatusId`s did NOT happen (e.g. the customer
// never showed) → `DidNotOccur`. `SessionStatusId` is the only reliable signal:
// `AttendedSession` is `false` even for completed/QC sessions, and the `Completed`
// bool is true only for status 11 (so it misses Quality Control, which DID happen).
//
// Raw ints by necessity: the source `SessionStatuses` enum lives in CADY Core API
// (`Cady.CoreApi.DataAccess/.../Enumerations/SessionStatuses.cs`), NOT the Sunny16
// project the storefront's TS types are generated from, so there's no generated
// enum to import here. Values are confirmed against real profile data (id+name
// paired): 2=Scheduled, 11=Completed, 12=Quality Control, 14=No Show. Seeded with
// No Show only; expand with Cancelled/etc. once the business confirms which
// additional statuses count as "did not happen". Any other past-date status →
// `PostSession`.
export const NON_ATTENDED_SESSION_STATUS_IDS: ReadonlySet<number> = new Set([
  14 // No Show
])

export interface SessionPhaseInput {
  // The session's appointment date (UTC). Null when unknown.
  sessionDateUtc: Date | string | null
  // The session's `SessionStatusId`. Consulted only on the past-date branch, to
  // split a no-show (`NON_ATTENDED_SESSION_STATUS_IDS`) from an attended session.
  // Null when unknown.
  sessionStatusId: number | null
  now: Date
}

// Whether `now` is still within the session day — through 11:59 PM ET on the
// session date. Null when the date is unknown. This is the pre/post boundary,
// shared by `resolveSessionPhase` and any display that needs the raw open/closed
// value (e.g. the preorder dry-run preview).
export function isWithinSessionDay(
  sessionDateUtc: Date | string | null,
  now: Date
): boolean | null {
  if (sessionDateUtc === null) {
    return null
  }
  const endOfSessionDay = dayjs(sessionDateUtc).tz(SESSION_DAY_TIMEZONE).endOf('day')
  return !dayjs(now).isAfter(endOfSessionDay)
}

// The temporal phase of the given session. Returns null when the session date is
// unknown — callers decide what an unknown phase means for their flow. Pure and
// deterministic given `now`, so it can be unit-tested in isolation.
//
// NOTE (ADR 2026.07.27-001): `input` describes ONE already-chosen session; callers
// own multi-session precedence (which of a subject's sessions to phase) upstream.
export function resolveSessionPhase(input: SessionPhaseInput): SessionPhase | null {
  const withinSessionDay = isWithinSessionDay(input.sessionDateUtc, input.now)
  if (withinSessionDay === null) {
    return null
  }
  if (withinSessionDay) {
    return SessionPhase.PreSession
  }
  // Past date: split on whether the session happened. Unknown status (null) falls
  // to `PostSession` so a data gap doesn't misclassify an attended session as a
  // no-show.
  if (
    input.sessionStatusId !== null &&
    NON_ATTENDED_SESSION_STATUS_IDS.has(input.sessionStatusId)
  ) {
    return SessionPhase.DidNotOccur
  }
  return SessionPhase.PostSession
}
